import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { PageHeader, LegalSection } from "@/components/PagePrimitives";
import Reveal from "@/components/Reveal";
import NewTermsOfUse from "@/components/NewTermsOfUse";
import { SITE, FINANCIAL_DISCLAIMER, PREMIUM_DISCLOSURE } from "@/config/site";

const EFFECTIVE = "July 2026";

export default function Terms() {
  return (
    <>
      <SEO title="Terms of Use | Nova" description="The terms governing your use of Nova, including subscriptions, the financial disclaimer and your responsibilities." path="/terms" />
      <PageHeader eyebrow="Legal" title="Terms of Use" subtitle={`The terms that govern your use of Nova. Operated by ${SITE.legalOwner}.`} updated={EFFECTIVE} />

      <section className="pb-24">
        <div className="container-nova max-w-3xl space-y-10">
          <Reveal>
            <NewTermsOfUse />
          </Reveal>

          <Reveal>
            <div className="relative flex items-center gap-4 py-2" role="separator" aria-label="Existing Terms and Additional Provisions" data-testid="terms-divider">
              <span className="h-px flex-1 bg-white/10" />
              <span className="label-eyebrow whitespace-nowrap text-white/50">Existing Terms and Additional Provisions</span>
              <span className="h-px flex-1 bg-white/10" />
            </div>
          </Reveal>

          <LegalSection id="acceptance" heading="1. Acceptance of terms">
            <p>By downloading, accessing or using Nova, you agree to these Terms of Use. If you do not agree, please do not use Nova.</p>
          </LegalSection>

          <LegalSection id="eligibility" heading="2. Eligibility">
            <p>You must be at least 18 years old (or the minimum legal age in your country) and able to form a legally binding contract to use Nova.</p>
          </LegalSection>

          <LegalSection id="accounts" heading="3. Account responsibilities & accurate information">
            <p>You are responsible for your account and for keeping your sign-in details secure. You agree to provide accurate information and to keep it up to date so that Nova's forecasts and insights are meaningful.</p>
          </LegalSection>

          <LegalSection id="use" heading="4. Permitted & prohibited use">
            <p>Use Nova only for your own personal budgeting and planning. You must not misuse Nova, attempt to disrupt it, access it unlawfully, or upload documents that do not belong to you.</p>
          </LegalSection>

          <LegalSection id="purpose" heading="5. Nova's purpose & financial disclaimer">
            <p>{FINANCIAL_DISCLAIMER}</p>
            <p>Nova is not a bank, lender, credit agency or financial adviser and does not provide regulated financial advice. Forecasts, estimated dates and insights are estimates only and may not reflect future results.</p>
          </LegalSection>

          <LegalSection id="ai" heading="6. AI-generated content limitations">
            <p>AI insights and the AI Coach generate suggestions based on the information you provide. Output may be incomplete or inaccurate and should not be relied on as professional advice. Always use your own judgement.</p>
          </LegalSection>

          <LegalSection id="statements" heading="7. Bank-statement upload responsibilities">
            <p>If you use bank-statement analysis, you are responsible for the documents you upload. Only upload statements that belong to you, and never upload documents belonging to another person.</p>
          </LegalSection>

          <LegalSection id="ip" heading="8. Intellectual property">
            <p>Nova's original software, name, logo, branding, design and content are owned by {SITE.legalOwner} and protected by law. Third-party libraries, frameworks, SDKs, AI models, open-source software and licensed assets used within Nova remain the property of their respective owners and licensors. You may not copy, modify or distribute Nova without permission. You keep ownership of the data you enter.</p>
          </LegalSection>

          <LegalSection id="availability" heading="9. Availability & changes">
            <p>We may update, change or discontinue features from time to time, and Nova may occasionally be unavailable for maintenance. We may update these terms; continued use after changes means you accept the updated terms.</p>
          </LegalSection>

          <LegalSection id="premium" heading="10. Nova Premium & subscriptions">
            <p>Nova Premium is available as Weekly, Monthly and Annual subscriptions. All three unlock the same Premium features. Only the billing duration differs.</p>
            <p>Nova Premium is available as Weekly (£1.99/week), Monthly (£3.99/month) and Annual (£29.99/year) subscriptions. All three unlock the same Premium features. Only the billing duration differs. Local App Store prices may vary by region.</p>
            <p>{PREMIUM_DISCLOSURE}</p>
            <ul className="mt-2 list-disc space-y-1.5 pl-5">
              <li>Purchases are processed through Apple In-App Purchase on iOS and charged to your Apple Account.</li>
              <li>Manage or cancel your subscription in your Apple subscription settings; cancellation normally takes effect at the end of the current billing period.</li>
              <li>Use <Link to="/support#restore" className="text-nova-cyan hover:underline">Restore Purchases</Link> to restore Premium on a new device.</li>
              <li>Refund requests are handled by Apple; Nova cannot directly issue Apple refunds.</li>
              <li>When a subscription expires, Premium features are locked while your data remains in your account.</li>
            </ul>
          </LegalSection>

          <LegalSection id="termination" heading="11. Suspension or termination">
            <p>We may suspend or terminate access if these terms are breached or to protect Nova and its users. You may stop using Nova and delete your account at any time.</p>
          </LegalSection>

          <LegalSection id="liability" heading="12. Limitation of liability">
            <p>To the fullest extent permitted by law, Nova and {SITE.legalOwner} are not liable for indirect or consequential losses, or for decisions you make based on Nova's estimates and insights. Nothing in these terms limits liability that cannot be limited by law.</p>
          </LegalSection>

          <LegalSection id="law" heading="13. Governing law">
            <p>These terms are governed by the laws of {SITE.governingLaw}, and the courts of that jurisdiction will have jurisdiction, subject to any mandatory consumer protections in your country of residence.</p>
          </LegalSection>

          <LegalSection id="contact" heading="14. Contact">
            <p>Questions about these terms? Email <a className="text-nova-cyan hover:underline" href={`mailto:${SITE.supportEmail}`}>{SITE.supportEmail}</a>.</p>
          </LegalSection>
        </div>
      </section>
    </>
  );
}
